import { zodResolver } from '@hookform/resolvers/zod';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { PasswordRules } from '@/components/auth/password-rules';
import { Button } from '@/components/atoms';
import { Field, FieldError, FieldLabel, InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/api-error';
import {
  changePasswordDefaultValues,
  changePasswordSchema,
  type ChangePasswordFormValues,
} from '@/lib/auth-schemas';
import { useChangePasswordMutation } from '@/query/auth';

import './change-password-form.scss';

function ChangePasswordForm() {
  const mutation = useChangePasswordMutation();
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: changePasswordDefaultValues,
    mode: 'onChange',
  });

  const newPasswordValue = useWatch({
    control,
    name: 'newPassword',
    defaultValue: '',
  });

  const onSubmit = (data: ChangePasswordFormValues) => {
    if (mutation.isPending) return;
    mutation.mutate(
      { oldPassword: data.oldPassword, newPassword: data.newPassword },
      {
        onSuccess: () => {
          toast.success('Password changed');
          reset(changePasswordDefaultValues);
        },
      },
    );
  };

  const errorMessage = mutation.isError
    ? getApiErrorMessage(mutation.error, 'Could not change your password.')
    : null;

  return (
    <form
      className="change-password-form"
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e);
      }}
      noValidate
    >
      <h2 className="change-password-form__heading">Change password</h2>

      <Field>
        <FieldLabel htmlFor="profile-old-password">Current password</FieldLabel>
        <InputGroup>
          <InputGroupInput
            id="profile-old-password"
            type={showOld ? 'text' : 'password'}
            autoComplete="current-password"
            aria-invalid={Boolean(errors.oldPassword)}
            {...register('oldPassword')}
          />
          <InputGroupAddon align="inline-end">
            <button
              type="button"
              onClick={() => {
                setShowOld(v => !v);
              }}
              aria-label={showOld ? 'Hide password' : 'Show password'}
            >
              {showOld ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
            </button>
          </InputGroupAddon>
        </InputGroup>
        {errors.oldPassword && (
          <FieldError>{errors.oldPassword.message}</FieldError>
        )}
      </Field>

      <Field>
        <FieldLabel htmlFor="profile-new-password">New password</FieldLabel>
        <InputGroup>
          <InputGroupInput
            id="profile-new-password"
            type={showNew ? 'text' : 'password'}
            autoComplete="new-password"
            aria-invalid={Boolean(errors.newPassword)}
            {...register('newPassword', {
              onBlur: () => {
                setIsPasswordFocused(false);
              },
            })}
            onFocus={() => {
              setIsPasswordFocused(true);
            }}
          />
          <InputGroupAddon align="inline-end">
            <button
              type="button"
              onClick={() => {
                setShowNew(v => !v);
              }}
              aria-label={showNew ? 'Hide password' : 'Show password'}
            >
              {showNew ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
            </button>
          </InputGroupAddon>
        </InputGroup>
        {errors.newPassword && (
          <FieldError>{errors.newPassword.message}</FieldError>
        )}
        {isPasswordFocused && newPasswordValue.length > 0 && (
          <PasswordRules password={newPasswordValue} />
        )}
      </Field>

      <Field>
        <FieldLabel htmlFor="profile-confirm-password">
          Confirm password
        </FieldLabel>
        <InputGroup>
          <InputGroupInput
            id="profile-confirm-password"
            type={showConfirm ? 'text' : 'password'}
            autoComplete="new-password"
            aria-invalid={Boolean(errors.confirmNewPassword)}
            {...register('confirmNewPassword')}
          />
          <InputGroupAddon align="inline-end">
            <button
              type="button"
              onClick={() => {
                setShowConfirm(v => !v);
              }}
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
            </button>
          </InputGroupAddon>
        </InputGroup>
        {errors.confirmNewPassword && (
          <FieldError>{errors.confirmNewPassword.message}</FieldError>
        )}
      </Field>

      {errorMessage && (
        <p className="change-password-form__error" role="alert">
          {errorMessage}
        </p>
      )}

      <Button
        className="change-password-form__button"
        type="submit"
        variant="default"
        disabled={!isValid || mutation.isPending}
      >
        {mutation.isPending ? 'Changing…' : 'Change password'}
      </Button>
    </form>
  );
}

export { ChangePasswordForm };
